import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { TrackingApiAdapter } from '../adapters/TrackingApiAdapter';
import type { OrderTracking } from '../models/Tracking';
import {
  faLocationSlash,
  faTruck,
  faBox,
  faWarehouse,
  faMapMarkerAlt,
  faCheck,
} from '@fortawesome/pro-light-svg-icons';

const httpClient = createHttpClient(apiClientWH);
const adapter = new TrackingApiAdapter(httpClient);

// Función para obtener el icono según el código del evento
export const getEventIcon = (code: string) => {
  switch (code) {
    case '12': // ENTREGADO
      return faCheck;
    case '8': // EN REPARTO
      return faTruck;
    case '6': // DELEGACION DESTINO
      return faWarehouse;
    case '2': // EN ARRASTRE
      return faBox;
    case '1': // SIN RECEPCION
      return faLocationSlash;
    default:
      return faMapMarkerAlt;
  }
};

// Convierte fecha DD/MM/YYYY y hora HH:mm:ss a timestamp
export const toTrackingEventTimestamp = (event: { date?: string; time?: string }): number => {
  if (!event?.date) return 0;
  const [day, month, year] = event.date.split('/').map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = (event.time || '00:00:00').split(':').map(Number);
  return new Date(year, (month || 1) - 1, day || 1, hours, minutes, seconds).getTime();
};

export const sortTrackingEventsByDateDesc = (events: OrderTracking['events']): OrderTracking['events'] => {
  return [...events].sort((a, b) => toTrackingEventTimestamp(b) - toTrackingEventTimestamp(a));
};

// Función para obtener el último evento de tracking
export const getLastTrackingEvent = (tracking?: OrderTracking) => {
  if (!tracking?.events || tracking.events.length === 0) {
    return null;
  }

  return tracking.events.reduce((latest, ev) => {
    return toTrackingEventTimestamp(ev) >= toTrackingEventTimestamp(latest) ? ev : latest;
  }, tracking.events[0]);
};

export const useOrderTracking = () => {
  const { orderId } = useParams<{ orderId: string }>();

  const query = useQuery<OrderTracking | undefined>({
    queryKey: ['orderTracking', orderId],
    queryFn: async () => {
      if (!orderId) return undefined;
      return adapter.getOrderTracking(Number(orderId));
    },
    enabled: Boolean(orderId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Obtener el último evento
  const lastEvent = getLastTrackingEvent(query.data);

  return {
    ...query,
    lastEvent,
    getEventIcon,
  };
};
